import { Badge, Button, Group, Text, Box, UnstyledButton, Alert } from '@mantine/core';
import { IconAlertTriangle, IconCrown, IconClock } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';

type SubscriptionStatusBadgeProps = {
  variant?: 'badge' | 'alert' | 'minimal';
  showUpgradeButton?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  iconSize?: number;
  onClick?: () => void;
};

export const SubscriptionStatusBadge = ({ 
  variant = 'badge', 
  showUpgradeButton = false,
  size = 'sm',
  iconSize = 14,
  onClick
}: SubscriptionStatusBadgeProps) => {
  const { subscription, hasAccess, getTrialDaysRemaining, isTrialExpiringSoon, isTrialCanceled } = useSubscription();
  const navigate = useNavigate();
  
  if (!subscription) return null;

  const trialDaysRemaining = getTrialDaysRemaining();
  
  // Don't show anything for active subscriptions in minimal mode
  if (variant === 'minimal' && subscription.status === 'active') {
    return null;
  }

  const getStatusInfo = () => {
    switch (subscription.status) {
      case 'active':
        return {
          color: 'green',
          label: subscription.planType === 'annual' ? 'Pro Annual' : 'Pro Monthly',
          icon: <IconCrown size={iconSize} />,
          urgent: false
        };
      case 'trial':
        const isExpired = trialDaysRemaining <= 0;
        // A canceled trial still has its remaining days, but the user has told
        // us they're done — so it reads as neutral and stops being urgent
        // rather than badgering them to subscribe.
        if (isTrialCanceled && !isExpired) {
          return {
            color: 'gray',
            label: `Trial canceled: ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left`,
            icon: <IconClock size={iconSize} />,
            urgent: false
          };
        }
        const isExpiring = isTrialExpiringSoon;
        return {
          color: isExpired ? 'red' : isExpiring ? 'yellow' : 'blue',
          label: isExpired ? 'Trial Expired' : `Trial: ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left`,
          icon: <IconClock size={iconSize} />,
          urgent: isExpired || isExpiring
        };
      case 'past_due':
        return {
          color: 'red',
          label: 'Payment Failed',
          icon: <IconAlertTriangle size={iconSize} />,
          urgent: true
        };
      case 'canceled':
        return {
          color: 'orange',
          label: 'Canceled',
          icon: <IconAlertTriangle size={iconSize} />,
          urgent: true
        };
      default:
        return null;
    }
  };

  const statusInfo = getStatusInfo();
  if (!statusInfo) return null;

  const handleUpgrade = () => {
    navigate('/billing');
  };

  // Badge variant
  if (variant === 'badge') {
    return (
      <Group gap="xs" wrap="nowrap" align="center">
        <Badge
          variant="light"
          color={statusInfo.color}
          leftSection={statusInfo.icon}
          size="sm"
        >
          {statusInfo.label}
        </Badge>
        {showUpgradeButton && statusInfo.urgent && (
          <Button size="xs" variant="light" color={statusInfo.color} onClick={handleUpgrade}>
            Upgrade
          </Button>
        )}
      </Group>
    );
  }

  // Alert variant
  if (variant === 'alert') {
    // For trial status that doesn't need much attention, don't show anything —
    // including a canceled trial that's winding down, where an "expiring soon"
    // nudge would just be arguing with a decision they already made.
    if (subscription.status === 'trial' && hasAccess && (!isTrialExpiringSoon || isTrialCanceled)) {
      return null;
    }
    
    // For active subscriptions, don't show anything
    if (subscription.status === 'active') {
      return null;
    }

    // For urgent situations (expiring trial, payment issues), use consistent styling
    return (
      <Alert
        icon={statusInfo.icon}
        color={statusInfo.color}
        radius="lg"
        style={{
          border: `1px solid var(--mantine-color-${statusInfo.color}-3)`,
        }}
      >
        <Group 
          justify="space-between" 
          align="center"
          
        >
          <Group align="center" gap="sm">
            <Box>
              <Text size="sm" fw={500} c={statusInfo.color}>
                {statusInfo.label}
              </Text>
              {subscription.status === 'trial' && !hasAccess && (
                <Text size="xs" c="dimmed">
                  Subscribe to continue using Meals You Love
                </Text>
              )}
              {subscription.status === 'trial' && hasAccess && isTrialExpiringSoon && (
                <Text size="xs" c="dimmed">
                  Subscribe now to avoid interruption
                </Text>
              )}
              {subscription.status === 'past_due' && (
                <Text size="xs" c="dimmed">
                  Please update your payment method
                </Text>
              )}
              {subscription.status === 'canceled' && (
                <Text size="xs" c="dimmed">
                  Your subscription has been canceled
                </Text>
              )}
            </Box>
          </Group>
          {(showUpgradeButton || statusInfo.urgent) && (
            <Button 
              size="sm" 
              variant="filled" 
              color={statusInfo.color}
              onClick={handleUpgrade}
              style={{ flexShrink: 0 }}
            >
              {subscription.status === 'trial' ? 'Subscribe Now' : 'Update Payment'}
            </Button>
          )}
        </Group>
      </Alert>
    );
  }

  // Minimal variant - just text with icon
  const content = (
    <Group gap="xs" wrap="nowrap" align="center">
      <Box style={{ 
        color: `var(--mantine-color-${statusInfo.color}-6)`,
        display: 'flex',
        alignItems: 'center'
      }}>
        {statusInfo.icon}
      </Box>
      <Text size={size} c={statusInfo.color} fw={500}>
        {statusInfo.label}
      </Text>
    </Group>
  );

  if (onClick) {
    return (
      <UnstyledButton onClick={onClick} style={{ display: 'block' }}>
        {content}
      </UnstyledButton>
    );
  }

  return content;
};
